/**
 * Sign in with Apple JS on the web (popup). Needs the Services ID below registered in the Apple developer portal
 * with Sign in with Apple enabled, domain handegg.hustlemania.dev and return URL APPLE_WEB_REDIRECT. The ID token's
 * `aud` is the Services ID; the server accepts it next to the bundle id (APPLE_WEB_SERVICES_ID in
 * apps/server-cf/src/account/apple-signin.ts). Like native: SHA-256(nonce) goes to Apple, the raw nonce to the server.
 */
export const APPLE_WEB_SERVICES_ID = 'com.hustlemania.handegg.web';
export const APPLE_WEB_REDIRECT = 'https://handegg.hustlemania.dev/';
const APPLE_JS = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleIdAuth {
  init(o: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
    nonce?: string;
    state?: string;
  }): void;
  signIn(): Promise<{
    authorization?: { id_token?: string; code?: string };
    user?: { name?: { firstName?: string; lastName?: string }; email?: string };
  }>;
}
type AppleWindow = Window & { AppleID?: { auth?: AppleIdAuth } };

export type AppleWebOutcome =
  | { ok: true; idToken: string; nonce: string; fullName: string | null }
  | { ok: false; reason: 'cancelled' | 'failed'; detail?: string };

let loading: Promise<AppleIdAuth> | null = null;
const load = (): Promise<AppleIdAuth> => {
  const have = (window as AppleWindow).AppleID?.auth;
  if (have) return Promise.resolve(have);
  if (loading) return loading;
  loading = new Promise<AppleIdAuth>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = APPLE_JS;
    s.async = true;
    s.onload = () => {
      const a = (window as AppleWindow).AppleID?.auth;
      if (a) resolve(a);
      else reject(new Error('appleid missing'));
    };
    s.onerror = () => {
      loading = null;
      reject(new Error('appleid load failed'));
    };
    document.head.appendChild(s);
  });
  return loading;
};

/** Preload the Apple JS so the click opens the popup synchronously enough for popup blockers. */
export const preloadAppleWeb = (): void => {
  void load().catch(() => undefined);
};

const hex = (b: ArrayBuffer): string =>
  [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');

/**
 * Apple's official rendered button: Apple JS draws "Sign in with Apple" into the element with id `appleid-signin`
 * (its data-* attributes set colour / size / radius), and a click opens the popup; the result comes back as
 * `AppleIDSignInOnSuccess` / `AppleIDSignInOnFailure` document events. Returns a disposer.
 */
export const mountAppleButton = (onResult: (r: AppleWebOutcome) => void): (() => void) => {
  let live = true;
  let raw = '';
  const ok = (e: Event): void => {
    if (!live) return;
    const d = (e as CustomEvent).detail as Awaited<ReturnType<AppleIdAuth['signIn']>>;
    const tok = d?.authorization?.id_token;
    if (!tok) return onResult({ ok: false, reason: 'failed', detail: 'no id_token' });
    const n = d.user?.name;
    const fullName = [n?.firstName, n?.lastName].filter(Boolean).join(' ') || null;
    onResult({ ok: true, idToken: tok, nonce: raw, fullName });
  };
  const fail = (e: Event): void => {
    if (!live) return;
    const err = ((e as CustomEvent).detail as { error?: string } | null)?.error;
    if (err === 'popup_closed_by_user' || err === 'user_cancelled_authorize')
      onResult({ ok: false, reason: 'cancelled' });
    else onResult({ ok: false, reason: 'failed', detail: err });
  };
  document.addEventListener('AppleIDSignInOnSuccess', ok);
  document.addEventListener('AppleIDSignInOnFailure', fail);
  void (async () => {
    try {
      const auth = await load();
      raw = hex(crypto.getRandomValues(new Uint8Array(32)).buffer as ArrayBuffer);
      const hashed = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw)));
      if (!live) return;
      auth.init({
        clientId: APPLE_WEB_SERVICES_ID,
        scope: 'name email',
        redirectURI: APPLE_WEB_REDIRECT,
        usePopup: true,
        nonce: hashed,
      });
    } catch {
      if (live) onResult({ ok: false, reason: 'failed', detail: 'appleid load failed' });
    }
  })();
  return () => {
    live = false;
    document.removeEventListener('AppleIDSignInOnSuccess', ok);
    document.removeEventListener('AppleIDSignInOnFailure', fail);
  };
};

export const signInWithAppleWeb = async (): Promise<AppleWebOutcome> => {
  try {
    const auth = await load();
    const raw = hex(crypto.getRandomValues(new Uint8Array(32)).buffer as ArrayBuffer);
    const hashed = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw)));
    auth.init({
      clientId: APPLE_WEB_SERVICES_ID,
      scope: 'name email',
      redirectURI: APPLE_WEB_REDIRECT,
      usePopup: true,
      nonce: hashed,
    });
    const r = await auth.signIn();
    const tok = r.authorization?.id_token;
    if (!tok) return { ok: false, reason: 'failed', detail: 'no id_token' };
    const n = r.user?.name;
    const fullName = [n?.firstName, n?.lastName].filter(Boolean).join(' ') || null;
    return { ok: true, idToken: tok, nonce: raw, fullName };
  } catch (e) {
    const err = (e as { error?: string } | null)?.error;
    if (err === 'popup_closed_by_user' || err === 'user_cancelled_authorize')
      return { ok: false, reason: 'cancelled' };
    return { ok: false, reason: 'failed', detail: err ?? (e instanceof Error ? e.message : String(e)) };
  }
};
